/**
 * Eventos internos del embudo → POST /api/funnel/event
 */

import { getOrCreateAnonymousId } from './anonymousId';
import { getStoredVideoAccess } from './videoAccessStorage';
import { getMetaSignals } from './metaCookies';

function normalizeBackendUrl(base) {
  return (base || 'http://localhost:3001').replace(/\/$/, '');
}

export function getEmailFromVideoAccess() {
  try {
    const data = getStoredVideoAccess();
    if (!data) return '';
    return (data.email || '').trim().toLowerCase();
  } catch {
    return '';
  }
}

/** Contacto completo guardado del lead, para enriquecer el matching de Meta. */
export function getContactFromVideoAccess() {
  try {
    const data = getStoredVideoAccess() || {};
    return {
      email: (data.email || '').trim().toLowerCase(),
      nombre: data.nombre || '',
      whatsapp: data.whatsapp || '',
    };
  } catch {
    return { email: '', nombre: '', whatsapp: '' };
  }
}

/**
 * Confirma en backend (y Google Sheet) si se pudo invocar fbq CompleteRegistration para un lead calificado.
 * No bloquea la UI; errores se ignoran.
 */
export function ackQualificationMetaPixel(backendUrl, email, metaCompleteRegistrationInvoked) {
  const e = String(email || '').trim().toLowerCase();
  if (!e) return;
  const url = `${normalizeBackendUrl(backendUrl)}/api/qualification-form/meta-pixel-ack`;
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: e,
        meta_complete_registration_invoked: Boolean(metaCompleteRegistrationInvoked),
      }),
      keepalive: true,
    })
      .then(async (res) => {
        if (!res.ok) {
          console.warn('[meta-pixel-ack] HTTP', res.status, url);
          return;
        }
        const data = await res.json().catch(() => ({}));
        if (data.updated === false) {
          console.warn(
            '[meta-pixel-ack] Google Sheet no actualizó columna P (email / Cualificado=SÍ / celda P=pendiente).',
            { email: e, invoked: metaCompleteRegistrationInvoked }
          );
        }
      })
      .catch((err) => console.warn('[meta-pixel-ack] red', err?.message || err));
  } catch (_) {}
}

/**
 * Envía el evento CompleteRegistration al backend (Conversions API de Meta).
 * Fire-and-forget: no bloquea la UI, los errores se ignoran silenciosamente.
 *
 * Manda las cookies del Pixel (_fbp/_fbc), el teléfono y el nombre porque son las
 * señales con las que Meta amarra la conversión al anuncio y al usuario. Con solo
 * el email, la calidad de coincidencia queda en el suelo.
 *
 * @param {string} backendUrl
 * @param {string} email     - Email del usuario cualificado
 * @param {string} eventId   - Mismo eventId que se pasó al Pixel para que Meta desduplique
 * @param {object} [extra]   - { nombre, whatsapp }
 */
export function sendCapiCompleteRegistration(backendUrl, email, eventId, extra = {}) {
  const e = String(email || '').trim().toLowerCase();
  if (!e || !eventId) return;
  const url = `${normalizeBackendUrl(backendUrl)}/api/qualification-form/capi-event`;
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: e,
        event_id: eventId,
        nombre: extra.nombre || '',
        whatsapp: extra.whatsapp || '',
        anonymous_id: getOrCreateAnonymousId(),
        source_url: typeof window !== 'undefined' ? window.location.href : '',
        ...getMetaSignals(),
      }),
      keepalive: true,
    })
      .then(async (res) => {
        if (!res.ok) console.warn('[meta-capi] HTTP', res.status, url);
      })
      .catch((err) => console.warn('[meta-capi] red', err?.message || err));
  } catch (_) {}
}

/**
 * Manda un evento de la whitelist (hoy solo Schedule) a la Conversions API.
 * Fire-and-forget con keepalive, para que sobreviva a la navegación inmediata.
 *
 * @param {string} backendUrl
 * @param {object} params - { eventName, eventId, email, nombre, whatsapp }
 */
export function sendCapiWhitelistedEvent(backendUrl, { eventName, eventId, email, nombre, whatsapp }) {
  if (!eventName || !eventId) return;
  const url = `${normalizeBackendUrl(backendUrl)}/api/meta/capi-event`;
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_name: eventName,
        event_id: eventId,
        email: String(email || '').trim().toLowerCase(),
        nombre: nombre || '',
        whatsapp: whatsapp || '',
        anonymous_id: getOrCreateAnonymousId(),
        source_url: typeof window !== 'undefined' ? window.location.href : '',
        ...getMetaSignals(),
      }),
      keepalive: true,
    })
      .then((res) => {
        if (!res.ok) console.warn('[meta-capi]', eventName, 'HTTP', res.status);
      })
      .catch((err) => console.warn('[meta-capi]', eventName, 'red', err?.message || err));
  } catch (_) {}
}

/**
 * @param {string} eventName - view_video | proceed_to_form | form_abandoned
 * @param {object} options - email (opcional), anonymousId (opcional; se genera si falta)
 */
export function trackInternalFunnelEvent(backendUrl, eventName, eventData = {}, options = {}) {
  const anonymousId = options.anonymousId || getOrCreateAnonymousId();
  const email = (options.email || '').trim().toLowerCase();

  const url = `${normalizeBackendUrl(backendUrl)}/api/funnel/event`;
  const body = JSON.stringify({
    anonymous_id: anonymousId,
    email: email || undefined,
    event_name: eventName,
    event_data: eventData,
  });

  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (_) {}
}
